import { supabase } from "@/integrations/supabase/client";

export interface StageRequirement {
  name: string;
  description: string;
  check: (jobId: string) => Promise<boolean>;
}

export interface StageValidationResult {
  canProgress: boolean;
  missingRequirements: string[];
  completedRequirements: string[];
}

// Define requirements for each stage based on stage_key
const STAGE_REQUIREMENTS: Record<string, StageRequirement[]> = {
  // Retail workflow
  'contacted': [],
  'appointment_set': [
    {
      name: 'Schedule Appointment',
      description: 'A calendar event must be scheduled',
      check: async (jobId: string) => {
        const { data, error } = await supabase
          .from('calendar_events')
          .select('id')
          .eq('job_id', jobId)
          .limit(1);
        return !error && (data?.length || 0) > 0;
      }
    }
  ],
  'proposal_sent': [
    {
      name: 'Create Proposal',
      description: 'At least one estimate or proposal must be created',
      check: async (jobId: string) => {
        const { data, error } = await supabase
          .from('job_estimates')
          .select('id')
          .eq('job_id', jobId)
          .limit(1);
        return !error && (data?.length || 0) > 0;
      }
    }
  ],
  'inspected': [
    {
      name: 'Create Proposal',
      description: 'At least one estimate or proposal must be created',
      check: async (jobId: string) => {
        const { data, error } = await supabase
          .from('job_estimates')
          .select('id')
          .eq('job_id', jobId)
          .limit(1);
        return !error && (data?.length || 0) > 0;
      }
    }
  ],
  
  // Insurance workflow
  'inspection': [
    {
      name: 'Inspection Photos',
      description: 'Upload at least one inspection photo',
      check: async (jobId: string) => {
        const { data, error } = await supabase
          .from('photos')
          .select('id')
          .eq('job_id', jobId)
          .limit(1);
        return !error && (data?.length || 0) > 0;
      }
    }
  ],
  'adjuster_meeting': [
    {
      name: 'Schedule Meeting',
      description: 'Schedule a meeting with the adjuster',
      check: async (jobId: string) => {
        const { data, error } = await supabase
          .from('calendar_events')
          .select('id')
          .eq('job_id', jobId)
          .limit(1);
        return !error && (data?.length || 0) > 0;
      }
    }
  ],
  'claim_approved': [
    {
      name: 'Claim Documentation',
      description: 'Upload claim approval documents or add claim analysis',
      check: async (jobId: string) => {
        const { data, error } = await supabase
          .from('claim_analyses')
          .select('id')
          .eq('job_id', jobId)
          .limit(1);
        return !error && (data?.length || 0) > 0;
      }
    }
  ],
  
  // Commercial workflow
  'site_visit': [
    {
      name: 'Site Photos',
      description: 'Document the site with photos',
      check: async (jobId: string) => {
        const { data, error } = await supabase
          .from('photos')
          .select('id')
          .eq('job_id', jobId)
          .limit(1);
        return !error && (data?.length || 0) > 0;
      }
    }
  ],
  'bidding': [
    {
      name: 'Roof Measurement',
      description: 'Complete roof measurements for bidding',
      check: async (jobId: string) => {
        const { data, error } = await supabase
          .from('measurements_roof')
          .select('id')
          .eq('job_id', jobId)
          .limit(1);
        return !error && (data?.length || 0) > 0;
      }
    }
  ],
  'proposal_submitted': [
    {
      name: 'Submit Proposal',
      description: 'Create and submit a detailed proposal',
      check: async (jobId: string) => {
        const { data, error } = await supabase
          .from('job_estimates')
          .select('id')
          .eq('job_id', jobId)
          .limit(1);
        return !error && (data?.length || 0) > 0;
      }
    }
  ],
  'contract_signed': [
    {
      name: 'Signed Contract',
      description: 'Upload signed contract or mark contract as signed',
      check: async (jobId: string) => {
        // Check for uploaded contract documents
        const { data, error } = await supabase
          .from('documents')
          .select('id')
          .eq('job_id', jobId)
          .or('document_type.eq.contract,document_type.eq.signed_contract')
          .limit(1);
        return !error && (data?.length || 0) > 0;
      }
    }
  ],
  
  // Common stages
  'in_progress': [
    {
      name: 'Work Started',
      description: 'Mark job as started or assign crew',
      check: async (jobId: string) => {
        const { data, error } = await supabase
          .from('crew_assignments')
          .select('id')
          .eq('job_id', jobId)
          .limit(1);
        return !error && (data?.length || 0) > 0;
      }
    }
  ],
};

/**
 * Validate if a job can progress to a specific stage
 */
export async function validateStageProgression(
  jobId: string,
  targetStageKey: string
): Promise<StageValidationResult> {
  const requirements = STAGE_REQUIREMENTS[targetStageKey] || [];
  
  if (requirements.length === 0) {
    return {
      canProgress: true,
      missingRequirements: [],
      completedRequirements: []
    };
  }

  const results = await Promise.all(
    requirements.map(async (req) => ({
      name: req.name,
      description: req.description,
      completed: await req.check(jobId)
    }))
  );

  const missing = results.filter(r => !r.completed);
  const completed = results.filter(r => r.completed);

  return {
    canProgress: missing.length === 0,
    missingRequirements: missing.map(r => r.description),
    completedRequirements: completed.map(r => r.name)
  };
}

/**
 * Get all requirements for a stage (for display purposes)
 */
export function getStageRequirements(stageKey: string): StageRequirement[] {
  return STAGE_REQUIREMENTS[stageKey] || [];
}
